import { Box, Button, Dialog, Stack, Typography } from "@mui/material";
import { MdCheckCircle, MdWarningAmber } from "react-icons/md";
import { ReactNode } from "react";

interface ConfirmDialogProps {
  open: boolean;
  titulo: string;
  /** Texto o nodo del cuerpo (permite resaltar el fardo en negrita, etc.). */
  description: ReactNode;
  /** Label del botón principal (acción destructiva). */
  confirmLabel: string;
  cancelLabel?: string;
  /** Variante visual del diálogo. Por defecto 'warning'. */
  variant?: "warning" | "success";
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Confirm Dialog - Mobile first responsive dialog
 * Designed with mobile-first approach, optimized for small screens with max-width xs
 * and proper spacing adjustments that scale to larger viewports.
 */
export default function ConfirmDialog({
  open,
  titulo,
  description,
  confirmLabel,
  cancelLabel = "Cancelar",
  variant = "warning",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const isSuccess = variant === "success";
  const iconColor = isSuccess ? "success.main" : "error.main";
  const Icon = isSuccess ? MdCheckCircle : MdWarningAmber;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 1, mx: 2 } }}
    >
      <Box sx={{ px: 3, pt: 3, pb: 2.5, textAlign: "center" }}>
        {/* ícono en círculo */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            mx: "auto",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: iconColor,
            border: 2,
            borderColor: iconColor,
          }}
        >
          <Icon size={26} />
        </Box>

        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
          {titulo}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, px: 1 }}
        >
          {description}
        </Typography>

        <Stack spacing={1.25}>
          <Button
            variant="contained"
            color={isSuccess ? "success" : "error"}
            fullWidth
            size="large"
            onClick={onConfirm}
            sx={{ py: 1.4, fontWeight: 700 }}
          >
            {confirmLabel}
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            size="large"
            onClick={onCancel}
            sx={{ py: 1.4 }}
          >
            {cancelLabel}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}
